import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Bell, Shield, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SettingsPage = () => {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    profileVisibility: 'public',
    dataSharing: false
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    fetchUserPreferences();
  }, [authLoading, isAuthenticated, navigate, user?.id]);

  const fetchUserPreferences = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/users/${user.id}`);
      if (response.data.preferences) {
        setPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences({ ...preferences, [key]: value });
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/${user.id}`, {
        preferences: preferences
      });
      
      toast({
        title: 'Settings Saved',
        description: 'Your preferences have been updated successfully.'
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type DELETE to confirm account deletion.',
        variant: 'destructive'
      });
      return;
    }
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}`);
      
      toast({
        title: 'Account Deleted',
        description: 'Your account has been permanently deleted.'
      });
      
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account. Please contact support.',
        variant: 'destructive'
      });
      setDeleteLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">Email Address</Label>
                <Input type="email" value={user?.email} disabled className="mt-1 bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Account Type</Label>
                <Input value={user?.userType === 'homeowner' ? 'Homeowner' : 'Professional'} disabled className="mt-1 bg-gray-50" />
              </div>
              {user?.userType === 'professional' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Subscription Plan</Label>
                  <div className="flex items-center justify-between mt-1">
                    <Input value={user?.subscriptionTier?.charAt(0).toUpperCase() + user?.subscriptionTier?.slice(1)} disabled className="bg-gray-50 flex-1 mr-3" />
                    <Button onClick={() => navigate('/subscription')} variant="outline">Manage Plan</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Bell className="h-5 w-5 text-orange-600 mr-2" />
                <CardTitle>Notification Preferences</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Email Notifications</p>
                  <p className="text-sm text-gray-600">Receive updates and alerts via email</p>
                </div>
                <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                  Coming Soon
                </div>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">SMS Notifications</p>
                  <p className="text-sm text-gray-600">Receive text messages for important updates</p>
                </div>
                <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                  Coming Soon
                </div>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900">Marketing Emails</p>
                  <p className="text-sm text-gray-600">Receive promotional content and offers</p>
                </div>
                <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                  Coming Soon
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-orange-600 mr-2" />
                <CardTitle>Privacy Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Profile Visibility</Label>
                <select value={preferences.profileVisibility} onChange={(e) => handlePreferenceChange('profileVisibility', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="public">Public - Anyone can view</option>
                  <option value="private">Private - Only you</option>
                  <option value="contacts">Contacts Only</option>
                </select>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900">Data Sharing</p>
                  <p className="text-sm text-gray-600">Share anonymous data for service improvement</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={preferences.dataSharing} onChange={(e) => handlePreferenceChange('dataSharing', e.target.checked)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSavePreferences} disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Save Settings'}
            </Button>
          </div>

          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center">
                <Trash2 className="h-5 w-5 text-red-600 mr-2" />
                <CardTitle className="text-red-600">Danger Zone</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-900 mb-1">Delete Account</h4>
                    <p className="text-sm text-red-700 mb-4">Once you delete your account, there is no going back. This will permanently delete your profile, quotes, and all associated data.</p>
                    <Button onClick={() => setDeleteModalOpen(true)} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">Delete My Account</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800"><strong>Warning:</strong> This action cannot be undone. All your data will be permanently deleted including:</p>
              <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
                <li>Profile information</li>
                <li>Quote requests and history</li>
                <li>Payment transactions</li>
                <li>All settings and preferences</li>
              </ul>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Type <strong>DELETE</strong> to confirm</Label>
              <Input value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} placeholder="DELETE" className="font-mono" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => {setDeleteModalOpen(false); setDeleteConfirmation('');}} variant="outline" className="flex-1" disabled={deleteLoading}>Cancel</Button>
              <Button onClick={handleDeleteAccount} className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={deleteLoading || deleteConfirmation !== 'DELETE'}>
                {deleteLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>) : 'Delete My Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
      <Toaster />
    </div>
  );
};

export default SettingsPage;